/**
 * design-tokens/themes.js — the single source of truth for every colour in
 * the app.
 *
 * This is Nocturne, the second redesign: a monochrome, dark-first system —
 * black ground, near-white type, glassy 48px controls with 16px corners,
 * colour reserved for status. It replaces the previous "Stencil" system
 * (violet ink on paper, hairline rules, near-zero radius, five accent-hue
 * themes) outright rather than adding a sixth theme alongside it — Nocturne
 * has exactly one identity with a light/dark mode, and running the two
 * visual languages side by side was never coherent. `THEME_IDS` therefore
 * has a single entry, `'tattoo-labs'` (the id is kept as-is, not renamed —
 * lib/theme.js's `DASHBOARD_THEME`, every `data-theme='tattoo-labs'`
 * selector, and the contrast/gallery tests all key off it, and renaming it
 * would touch every one of those for no visual benefit).
 *
 * Two layers, read by three consumers (generateCSS() below, the contrast
 * test, and DesignGallery.jsx) — nothing else hand-copies a colour value:
 *  - PRIMITIVES: raw hex, straight from the Nocturne design system's own
 *    tokens/colors.css, one set per mode. Never read by a component
 *    directly.
 *  - SEMANTIC tokens (buildSemanticTokens): the role names components and
 *    CSS actually use — surface/surfaceRaised/text/textMuted/accent/
 *    onAccent/border/focusRing/danger/success/warning, the tint pairs pills
 *    need, and the glass-control recipe's own tokens (controlBorder,
 *    controlFillFrom/To, ringAccent). Every value here was verified against
 *    WCAG 2 AA with colorjs.io before being hardcoded — see
 *    tests/design/contrast.test.js, which re-verifies this file on every
 *    run rather than trusting that this comment stays true.
 */

/**
 * Raw values, straight from the Nocturne design system's tokens/colors.css.
 * `success`/`warning` drop that file's own alpha suffix on green-11/yellow-11
 * (`#46fea5d4`/`#fee949f5`, ~83%/~96% opaque) down to a solid hex — a status
 * colour used as plain text needs to be fully opaque to contrast-check
 * meaningfully; the near-total opacity in the source was presentational, not
 * a deliberate translucency.
 *
 * @type {Record<'light'|'dark', Record<string, string>>}
 */
export const PRIMITIVES = {
  dark: {
    bg: '#000000',
    surface: '#141517', // gray-1, the one raised-card surface
    text: '#f0f0f0', // gray-12
    muted: '#a1a4a5', // gray-11
    border: '#212629', // gray-3, the hairline divider
    borderStrong: '#6e7679', // gray-9 — the darkest neutral that still clears 3:1 against surface-raised; for boundaries that need to read as a boundary even without the focus ring (a dashed dropzone outline, mainly — see borderStrong's own note below)
    focusRing: '#878d8f', // gray-10
    accent: '#ffffff', // bg-accent
    accentHover: '#f0f0f0', // bg-accent-hover
    onAccent: '#000000', // text-on-accent
    danger: '#ff9592', // red-11
    success: '#46fea5', // green-11
    warning: '#fee949', // yellow-11
    controlBorder: '#ffffff0d',
    controlFillFrom: '#fdfdfd0d',
    controlFillTo: '#f0f0e41a',
    ringAccent: '#caecff33',
  },
  light: {
    bg: '#ffffff',
    surface: '#f0f0f0', // gray-1
    text: '#191919', // gray-12
    muted: '#4d4d4d', // gray-11
    border: '#d7d7d7', // gray-3
    borderStrong: '#7e7e7e', // gray-6 — the darkest-clearing neutral for the same 3:1 boundary, see the dark-mode entry's note
    focusRing: '#3a3a3a', // gray-10
    accent: '#000000', // bg-accent
    accentHover: '#000000e6', // bg-accent-hover
    onAccent: '#ffffff', // text-on-accent
    danger: '#c40006', // red-11
    success: '#006514', // green-11
    warning: '#9e6c00', // yellow-11
    controlBorder: '#00000014',
    controlFillFrom: '#0000000a',
    controlFillTo: '#00000012',
    ringAccent: '#00000033',
  },
};

/**
 * Pre-computed status tint pairs (pill/badge backgrounds + border), one per
 * mode. Nocturne's own `-a4` tokens are translucent overlays tuned for a
 * badge sitting on a card, not for "status colour used as text against this
 * exact background clears 4.5:1" — the actual floor this app's contrast test
 * holds every other pairing to. So these are computed here, the same way
 * the previous Stencil palette's tints were: solid colour-mixes of the
 * status hue into the page background (`oklab`, matching every other mix in
 * this file), picked as the strongest tint that still clears 4.5:1 for the
 * status colour read as text on top of it. `-border` is a stronger mix of
 * the same hue, for definition only — never contrast-tested itself, since
 * nothing reads text off the border.
 *
 * Dark mode needs ~45-49% of the hue mixed into black: Nocturne's dark-mode
 * status colours are bright pastels (e.g. `#ff9592`), so the background has
 * to move a long way off pure black before there's enough gap to read them
 * against. Light mode's `warning` is a real outlier: its ochre
 * (`#9e6c00`) only clears 4.5:1 against a background at 100% white — i.e.
 * *any* amount of tint at all drops it below the floor (verified: even
 * `#fff394`, a 45%-white tint, is 4.02:1). That's a property of this one hue
 * at this one lightness, not a bug in the mixing — the pill's fill is left
 * at the page's own white for that one case (effectively no visible tint,
 * same intent as `transparent` but a real, testable colour rather than a
 * keyword the contrast test's `Color()` calls can't parse), same as its
 * `danger`/`success` siblings not being ordinary CSS shadows. The border
 * tint stands in for the missing fill instead.
 */
const TINTS = {
  dark: {
    successTint: '#1a7147',
    successTintBorder: '#37cd84',
    warningTint: '#71671b',
    warningTintBorder: '#cdbc39',
    dangerTint: '#663837',
    dangerTintBorder: '#c16f6d',
  },
  light: {
    successTint: '#bdd3bc',
    successTintBorder: '#78a477',
    warningTint: '#ffffff',
    warningTintBorder: '#e2d2bb',
    dangerTint: '#fad3cd',
    dangerTintBorder: '#eb9488',
  },
};

export const THEME_IDS = /** @type {const} */ (['tattoo-labs']);
export const MODES = /** @type {const} */ (['light', 'dark']);

/** Display name — kept for DesignGallery.jsx's existing lookup. */
export const THEME_LABELS = { 'tattoo-labs': 'Tattoo Lab' };

/**
 * Builds the full semantic token set for one mode — the plain object both
 * generateCSS() and the contrast test read. Never hand-write a second copy
 * of these values anywhere else.
 *
 * `themeId` is accepted (rather than dropped) purely so every existing call
 * site (`buildSemanticTokens('tattoo-labs', mode)`, in the contrast test and
 * DesignGallery.jsx) keeps working unchanged; there is only one theme now, so
 * any value other than `'tattoo-labs'` throws.
 *
 * @param {'tattoo-labs'} themeId
 * @param {'light'|'dark'} mode
 * @returns {Record<string, string>} CSS custom-property values, keyed by
 *   name without the leading `--`.
 */
export function buildSemanticTokens(themeId, mode) {
  if (themeId !== 'tattoo-labs') throw new Error(`Unknown theme: ${themeId}`);
  const p = PRIMITIVES[mode];
  if (!p) throw new Error(`Unknown mode: ${mode}`);
  const tint = TINTS[mode];

  return {
    // --- Canonical semantic tokens ----------------------------------------
    'surface-raised': p.surface,
    text: p.text,
    'text-muted': p.muted,
    accent: p.accent,
    'accent-hover': p.accentHover,
    'on-accent': p.onAccent,
    border: p.border,
    'border-strong': p.borderStrong,
    'focus-ring': p.focusRing,
    danger: p.danger,
    success: p.success,
    warning: p.warning,
    'success-tint': tint.successTint,
    'success-tint-border': tint.successTintBorder,
    'warning-tint': tint.warningTint,
    'warning-tint-border': tint.warningTintBorder,
    'danger-tint': tint.dangerTint,
    'danger-tint-border': tint.dangerTintBorder,

    // --- Glass control recipe (every button/input shares this) -----------
    'control-border': p.controlBorder,
    'control-fill-from': p.controlFillFrom,
    'control-fill-to': p.controlFillTo,
    'ring-accent': p.ringAccent,
    'control-lg': '48px',
    'control-md': '40px',
    radius: '16px', // radius-lg — every control and card
    'radius-sm': '8px', // small icon buttons inside fields
    'rule-width': '1px',

    // Legacy names — the ~30 existing selectors in styles.css/ui.css that
    // predate the canonical names above still read these; carrying both
    // means this redesign changes colour values, not every call site.
    bg: p.bg,
    surface: p.surface,
    muted: p.muted,
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)', // Nocturne's shadow-sm, same in both modes
  };
}

/** Renders a token object as `  --name: value;` lines, one per entry. */
function declBlock(tokens, indent = '  ') {
  return Object.entries(tokens)
    .map(([k, v]) => `${indent}--${k}: ${v};`)
    .join('\n');
}

/**
 * Generates the full stylesheet text.
 *
 * Theme identity (`data-theme`, always `'tattoo-labs'` now) and mode
 * (`data-mode`, or its absence — light, dark, **or system**) are
 * independent, so the one theme gets three rule shapes:
 *  1. A base selector with no mode requirement, holding the LIGHT tokens —
 *     covers "no explicit mode chosen, OS prefers light" and doubles as the
 *     safe default before JS has run at all.
 *  2. `@media (prefers-color-scheme: dark) { [data-theme='tattoo-labs']:not([data-mode='light']) }`
 *     — "no explicit mode chosen, OS prefers dark." The `:not(...)` means an
 *     explicit light choice still wins even under a dark OS.
 *  3. `[data-theme='tattoo-labs'][data-mode='dark']` — explicit dark, any OS
 *     setting, highest specificity of the three so it always wins.
 *
 * @returns {string} Plain CSS text, injected once by apply-theme.js.
 */
export function generateCSS() {
  const blocks = [];

  const lightTokens = buildSemanticTokens('tattoo-labs', 'light');
  const darkTokens = buildSemanticTokens('tattoo-labs', 'dark');

  blocks.push(`:root,\n[data-theme='tattoo-labs'] {\n  color-scheme: light;\n${declBlock(lightTokens)}\n}`);
  blocks.push(
    `@media (prefers-color-scheme: dark) {\n  [data-theme='tattoo-labs']:not([data-mode='light']) {\n    color-scheme: dark;\n${declBlock(darkTokens, '    ')}\n  }\n}`,
  );
  blocks.push(`[data-theme='tattoo-labs'][data-mode='dark'] {\n  color-scheme: dark;\n${declBlock(darkTokens)}\n}`);

  // --- Studio brand accent: safe derivation, one formula per mode --------
  //
  // A studio can set its own accent colour (studios.accent_color — see
  // src/lib/studio-accent.js, applied on the three public pages). It needs a
  // safe variant so a studio can't pick an unreadable colour, and — because
  // Nocturne's own `--accent` is literally black-on-white / white-on-black —
  // there is no single "darken toward black" formula that works in both
  // modes: a studio typing pure black (`#000000`) darkens toward itself and
  // stays unreadable as dark-mode text (verified: only 3.50:1 against
  // `bg`, 3.04:1 against `surface-raised`, both under the 4.5:1 floor),
  // exactly mirroring pure white failing the same way against a light
  // background. Each mode therefore mixes toward its OWN extreme instead of
  // always toward black:
  //   - light mode: 50% of the raw colour, 50% black (unchanged from the
  //     original Stencil-era formula — still verified safe below).
  //   - dark mode: 38% of the raw colour, 62% white. The stronger mix
  //     (62% vs. light mode's 50%) is because the worst case is harder to
  //     fix in this direction: `#000000` needs pushing to ~60% white before
  //     it clears 4.5:1 against `surface-raised` (the binding constraint,
  //     tighter than against `bg`) — verified with margin at 62%; every
  //     other colour a studio might pick needs less push than pure black
  //     does, the same "test the worst case, not the average case" logic
  //     the old dark-mode accent-lightening fix (progress.md) already used.
  //
  // `color-mix(in oklab, ...)` not the `oklch(from var(--brand) ...)`
  // relative-colour syntax some guidance sketches: browsers don't yet
  // implement gamut mapping for relative colour syntax, so adjusting only
  // the lightness channel there is unpredictable; mixing toward a fixed
  // white/black endpoint always stays in gamut.
  //
  // Three rule shapes again, exactly mirroring the theme tokens' own
  // light-default / media-override / explicit-override shape above, so an
  // explicit light/dark choice always outranks the system preference and
  // the two modes' formulas never both apply to the same element:
  //  1. `[data-studio-accent]:not([data-mode='dark'])` — light formula,
  //     covers "no mode chosen, OS light" and "mode explicitly light".
  //  2. `@media (prefers-color-scheme: dark) { [data-studio-accent]:not([data-mode='light']) }`
  //     — dark formula for "no mode chosen, OS dark".
  //  3. `[data-studio-accent][data-mode='dark']` — dark formula, explicit,
  //     any OS. Same specificity as (1) and (2) (two attribute selectors
  //     each); placed last so source order gives it the win for an element
  //     that could otherwise match more than one of these three, the same
  //     tie-break the base theme blocks above already rely on.
  //
  // `!important` on the declaration (not the rule) for the same reason the
  // original formula needed it: `[data-studio-accent]` alone is specificity
  // (0,1,0), while the dark-mode token blocks above are (0,2,0) — a real,
  // deliberate override of a rule that otherwise wins on specificity alone,
  // not decoration.
  blocks.push(
    [
      `@supports (color: color-mix(in oklab, red, blue)) {`,
      `  [data-studio-accent]:not([data-mode='dark']) {`,
      `    --accent: color-mix(in oklab, var(--studio-accent-raw) 50%, black 50%) !important;`,
      `  }`,
      `  @media (prefers-color-scheme: dark) {`,
      `    [data-studio-accent]:not([data-mode='light']) {`,
      `      --accent: color-mix(in oklab, var(--studio-accent-raw) 38%, white 62%) !important;`,
      `    }`,
      `  }`,
      `  [data-studio-accent][data-mode='dark'] {`,
      `    --accent: color-mix(in oklab, var(--studio-accent-raw) 38%, white 62%) !important;`,
      `  }`,
      `}`,
    ].join('\n'),
  );

  return blocks.join('\n\n');
}
